/**
 * auth.ts
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Secured Communications. All rights reserved.
 *
**/
"use strict";

import { DateTime } from "luxon";
import moment from "moment-timezone";

export const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,11}$/;

export interface Account {
  _id: string,
  partnerID: string,
  accountname: string,
  forceGroupArchiving: boolean,
  allowedStorage: number,
  totalStored: number,
  created: moment.Moment | string,
  kyberKey?: string,
  allowedFreeMemberships?: number,

  // client-side only
  remainingFreeMemberships?: number
}

export enum AuthType {
  webauthn = "authn",
  totp = "totp",
  googAuth = "goog",
  fb = "fb",
  linkedin = "linkedin"
}
export enum AuthDevType {
  yubikey = "yubikey",
  passkey = "passkey",
  biometric = "biometric"
}
export interface ThirdPartyAuth {
  _id: string,
  idtype: AuthType,
  authname: string,
  identity: Uint8Array | string,
  attachment: "cross-platform" | "platform" | undefined,
  devtype?: AuthDevType

  //client-only
  editing?: boolean,
  originalName?: string
}

export interface User {
  _id?: string,
  firstname: string,
  lastname: string,
  emails: Array<string>,
  perms: UserScopes,
  created: moment.Moment,
  auths?: Array<ThirdPartyAuth>
}

export enum PermissionLevel {
  NONE = 0,
  READ = 1,
  WRITE = 2,
  RW = 3,
  ADMIN = 4
}

export interface UserScopes {
  profile?: PermissionLevel
}

export interface AccountScopes {
  acct?: PermissionLevel,
  profiles?: PermissionLevel,
  groups?: PermissionLevel,
  files?: PermissionLevel,
  meetings?: PermissionLevel,
  archive?: PermissionLevel
}

export interface AllowedScopes extends UserScopes, AccountScopes { }

export enum MemberType {
  paid = "paid",
  assoc = "assoc",    // unused
  guest = "guest",
  temp = "temp"
}

export enum MemberRole {
  admin = "adm",
  manager = "mgr",
  user = "user",
  guest = "guest"
}

export enum BillingType {
  // default types
  paid = "paid",
  guest = "guest",

  // blofeld specific types
  free = "free"
}

export interface Membership {
  _id: string,
  regionID: string,
  partnerID: string,
  accountID: string,
  userID: string,
  membertype: MemberType,
  memberrole: MemberRole,
  billingType: BillingType,
  accountname: string,
  canArchive?: boolean,
  mustArchive?: boolean,
  firstname: string,
  lastname: string,
  email?: string,
  perms: Partial<AllowedScopes>,
  avatarUrl?: string,
  sendOnReturn?: boolean,
  timezone: string,
  created: moment.Moment,
  revoked?: moment.Moment,
  isComp?: boolean,

  // client-side only properties
  buttonText?: string,
  avatarText?: string
}

export enum DeviceType {
  web = "web",
  desktop = "desktop",
  mobile = "mobile",
  watch = "watch",
  tv = "tv"
}

/*export enum PushNotificationType {
  web = "web",
  apns = "apns"
}*/
export interface DeviceNode {
  _id: string,
  userID: string,
  publicKey?: string,
  kyberKey?: string,
  verifyKey?: string,
  protoVersion?: number,

  // client-side only
  groupMemberType?: string // eases adding multiple devices to a group at once
}

export interface Device extends DeviceNode {
  _id: string,
  localID?: ArrayBuffer,
  devicetype: DeviceType,
  userID: string,
  publicKey?: string,
  kyberKey?: string,
  verifyKey?: string, // dilithium public
  dbKey?: ArrayBuffer,
  dbNonce?: ArrayBuffer,
  dbVersion: number,
  protoVersion: number,
  minProtoVersion: number,
  userAgent?: string,
  os?: string,
  platform?: string,
  browser?: string,
  browserVersion?: string,
  displayString?: string,
  created?: moment.Moment,
  modified?: moment.Moment,
  isDead?: boolean,
  refreshTokenLength?: number,
  sessionTimeout?: number,

  // for posting to server only
  pushSubscription?: string,

  // client-side only
  groupMemberType?: string // eases adding multiple devices to a group at once
}

export enum InviteType {
  individual = "indv",
  mass = "mass"
}

export interface AccountInvite {
  _id: string,
  partnerID: string,
  accountID: string,
  clientID: string,
  userID?: string,
  invitetype: InviteType,
  accountname: string,
  membertype: MemberType,
  memberrole: MemberRole,
  billingType?: BillingType,
  email: string,
  campaignName: string,
  perms: AccountScopes,
  groupIDs?: Array<string>,
  huddleGroupID?: string,
  validAfter: DateTime,
  validBefore: DateTime,
  created: DateTime,
  compIfPossible?: boolean
}

export interface Team {
  _id?: string,
  teamColor: string,
  teamName: string,
  teamDesc: string,
  memberCount: number
}



